import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CalendarDays, Download } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import RevenueChart from '../../components/RevenueChart.jsx'
import RadarChart from '../../components/RadarChart.jsx'
import BarChartH from '../../components/BarChartH.jsx'
import {
  TH_MON_ABBR, TH_MON_FULL, DAILY_LIMIT, GREEN, SLATE, BLUE, PALETTE, RADAR_PRESETS,
  pad, toISO, daysBetween, fmt, enumMonths, bucketsFor, presetRange, comparePrevRange, compareLabel, comparePrevLabel, asList,
  Card, Segmented, Toggle, PresetBar, DateRange, MultiSelect, Stat, Delta, ChartLoading, Notice, todayISO,
} from './statShared.jsx'

// daily rows carry y/m/d; monthly rows carry y/m only — both bucket cleanly
const rowKey = (gran, r) => (gran === 'month' ? `${r.year}-${pad(r.month)}` : `${r.year}-${pad(r.month)}-${pad(r.day)}`)

// new-patient data only starts มิ.ย. 2024 → the all-time chart begins there
const BOTTOM_START = '2024-06-01'
// Tooth Taxi (mobile unit) leads come from company HR, not ads/marketing — exclude
// it by default so the new-patient view reflects real ad/campaign acquisition.
const TOOTHTAXI_BRANCH = '9'
const GROUPS = [{ v: 'branch', label: 'สาขา' }, { v: 'from', label: 'ช่องทาง' }, { v: 'type', label: 'ประเภทนัด' }]
const dimValue = (gb, r) => (gb === 'branch' ? String(r.branch) : gb === 'from' ? (r.from || '-') : String(r.appointmentList))

// CSV (UTF-8 BOM so Excel reads Thai); rows = array of arrays, first row = header
const csvEscape = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
function downloadCsv(filename, rows) {
  const text = '﻿' + rows.map((r) => r.map(csvEscape).join(',')).join('\r\n')
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8;' }))
  const a = document.createElement('a')
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(url)
}

export default function NewPatientStat({ excludeNhso = true, campaigns = null }) {
  // campaign mode: restrict the ประเภทนัด dimension to a fixed marketing-campaign
  // list (so every chart is filtered to campaign patients), and always include
  // สปสช (it's one of the campaigns).
  const campaignMode = Array.isArray(campaigns) && campaigns.length > 0
  const singleCampaign = campaignMode && campaigns.length === 1
  const effExcludeNhso = campaignMode ? false : excludeNhso
  const isOrtho = singleCampaign && campaigns[0].stat === 'ortho'
  const today = useMemo(() => todayISO(), [])  // Bangkok, not the browser clock
  const [branchName, setBranchName] = useState({})
  const [typeName, setTypeName] = useState({})

  // persist filters per context (main tab vs each campaign) so refresh restores them
  const pfx = campaignMode ? (singleCampaign ? `stat:np:${campaigns[0].stat}` : 'stat:np:campall') : 'stat:np:main'
  const init = useMemo(() => presetRange('1mo'), [])
  const [start, setStart] = usePersistedState(`${pfx}:start`, init[0])
  const [end, setEnd] = usePersistedState(`${pfx}:end`, init[1])
  const [preset, setPreset] = usePersistedState(`${pfx}:preset`, '1mo')
  const [groupBy, setGroupBy] = usePersistedState(`${pfx}:groupBy`, 'branch') // branch | from | type — default สาขา
  const [mode, setMode] = usePersistedState(`${pfx}:mode2`, 'compare')          // sum | split | compare (default เทียบ)
  const [radarCompare, setRadarCompare] = usePersistedState(`${pfx}:radarCompare2`, true)
  const [exporting, setExporting] = useState(false)
  const [exportErr, setExportErr] = useState('')   // shown inline by the CSV button (no alert())

  // store DISABLED ids → newly-loaded values are enabled by default
  // (Tooth Taxi is off by default — see TOOTHTAXI_BRANCH note above)
  const [branchOff, setBranchOff] = usePersistedState(`${pfx}:branchOff`, () => new Set([TOOTHTAXI_BRANCH]))
  const [sourceOff, setSourceOff] = usePersistedState(`${pfx}:sourceOff`, () => new Set())
  const [typeOff, setTypeOff] = usePersistedState(`${pfx}:typeOff`, () => new Set())

  const [topRows, setTopRows] = useState(null)
  const [topState, setTopState] = useState({ loading: true, err: '' })
  const [loadedGran, setLoadedGran] = useState(null)
  const [cmpRows, setCmpRows] = useState(null)
  const [cmpState, setCmpState] = useState({ loading: false, err: '' })
  const [cmpLoadedGran, setCmpLoadedGran] = useState(null)

  const [monthRows, setMonthRows] = useState(null)
  const [monthState, setMonthState] = useState({ loading: true, err: '' })

  const [orthoTopRows, setOrthoTopRows] = useState(null)
  const [orthoTopState, setOrthoTopState] = useState({ loading: false, err: '' })
  const [orthoLoadedGran, setOrthoLoadedGran] = useState(null)
  const [orthoMonthRows, setOrthoMonthRows] = useState(null)
  const [orthoMonthState, setOrthoMonthState] = useState({ loading: false, err: '' })

  const rangeInvalid = end < start
  const rangeDays = daysBetween(start, end) + 1
  const gran = rangeDays > DAILY_LIMIT ? 'month' : 'day'
  const endpoint = gran === 'month' ? '/api/appointment/monthly' : '/api/appointment/daily'
  const isRadar = RADAR_PRESETS.includes(preset)
  const wantCompare = isRadar ? radarCompare : mode === 'compare'
  const topReady = topRows != null && loadedGran === gran
  const cmpReady = cmpRows != null && cmpLoadedGran === gran
  const orthoTopReady = orthoTopRows != null && orthoLoadedGran === gran
  const orthoTopSum = useMemo(() => (orthoTopRows || []).reduce((s, r) => s + (r.count || 0), 0), [orthoTopRows])
  const orthoMonthSum = useMemo(() => (orthoMonthRows || []).reduce((s, r) => s + (r.count || 0), 0), [orthoMonthRows])

  const applyPreset = (key) => { const [s, e] = presetRange(key); setStart(s); setEnd(e); setPreset(key) }

  useEffect(() => {
    cachedGet('/api/branches')
      .then((br) => setBranchName(Object.fromEntries(asList(br).map((b) => [String(b.id ?? b.branch_id), b.name || b.branch_name || `สาขา #${b.id}`]))))
      .catch(() => {})
    cachedGet('/api/appointment-types')
      .then((ts) => setTypeName(Object.fromEntries(asList(ts).map((t) => [String(t.id), (t.detail || t.type || `#${t.id}`).trim()]))))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (rangeInvalid) return
    let alive = true
    setTopState({ loading: true, err: '' })
    apiGet(endpoint, { start_date: start, end_date: end, branch_id: 0, exclude_nhso: effExcludeNhso })
      .then((rows) => { if (alive) { setTopRows(Array.isArray(rows) ? rows : []); setLoadedGran(gran); setTopState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setTopRows([]); setLoadedGran(gran); setTopState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [endpoint, start, end, rangeInvalid, gran, effExcludeNhso])

  useEffect(() => {
    if (!wantCompare || rangeInvalid) { setCmpRows(null); return }
    let alive = true
    const [ps, pe] = comparePrevRange(preset, start, end)
    setCmpState({ loading: true, err: '' })
    apiGet(endpoint, { start_date: ps, end_date: pe, branch_id: 0, exclude_nhso: effExcludeNhso })
      .then((rows) => { if (alive) { setCmpRows(Array.isArray(rows) ? rows : []); setCmpLoadedGran(gran); setCmpState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setCmpRows([]); setCmpLoadedGran(gran); setCmpState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [wantCompare, preset, endpoint, start, end, rangeInvalid, gran, effExcludeNhso])

  // bottom all-time — dedicated monthly endpoint
  useEffect(() => {
    let alive = true
    setMonthState({ loading: true, err: '' })
    apiGet('/api/appointment/monthly', { start_date: BOTTOM_START, end_date: today, branch_id: 0, exclude_nhso: effExcludeNhso })
      .then((rows) => { if (alive) { setMonthRows(Array.isArray(rows) ? rows : []); setMonthState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setMonthRows([]); setMonthState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [today, effExcludeNhso])

  // ortho attach — top range (daily or monthly based on gran)
  useEffect(() => {
    if (!isOrtho || rangeInvalid) { setOrthoTopRows(null); setOrthoLoadedGran(null); return }
    let alive = true
    setOrthoTopState({ loading: true, err: '' })
    const ep = gran === 'month' ? '/api/appointment/ortho_attach/monthly' : '/api/appointment/ortho_attach/daily'
    apiGet(ep, { start_date: start, end_date: end })
      .then((rows) => { if (alive) { setOrthoTopRows(Array.isArray(rows) ? rows : []); setOrthoLoadedGran(gran); setOrthoTopState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setOrthoTopRows([]); setOrthoLoadedGran(gran); setOrthoTopState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [isOrtho, gran, start, end, rangeInvalid])

  // ortho attach — all-time monthly (for bottom chart)
  useEffect(() => {
    if (!isOrtho) { setOrthoMonthRows(null); return }
    let alive = true
    setOrthoMonthState({ loading: true, err: '' })
    apiGet('/api/appointment/ortho_attach/monthly', { start_date: BOTTOM_START, end_date: today })
      .then((rows) => { if (alive) { setOrthoMonthRows(Array.isArray(rows) ? rows : []); setOrthoMonthState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setOrthoMonthRows([]); setOrthoMonthState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [isOrtho, today])

  // dimension catalogs discovered from all loaded rows
  const dims = useMemo(() => {
    const b = new Set(), s = new Set(), t = new Set()
    for (const arr of [topRows, monthRows, cmpRows]) for (const r of arr || []) { b.add(String(r.branch)); s.add(r.from || '-'); t.add(String(r.appointmentList)) }
    return {
      // branch 0 = patients with no branch assignment (legacy/online) → exclude
      branch: [...b].filter((id) => id && id !== '0').sort((x, y) => Number(x) - Number(y)).map((id, i) => ({ id, label: branchName[id] || `สาขา #${id}`, color: PALETTE[i % PALETTE.length] })),
      from: [...s].filter(Boolean).sort().map((v, i) => ({ id: v, label: v, color: PALETTE[i % PALETTE.length] })),
      type: campaignMode
        ? campaigns.map((c, i) => ({ id: c.stat, label: c.label, color: PALETTE[i % PALETTE.length] }))
        : [...t].filter(Boolean).sort().map((id, i) => ({ id, label: typeName[id] || id, color: PALETTE[i % PALETTE.length] })),
    }
  }, [topRows, monthRows, cmpRows, branchName, typeName, campaignMode, campaigns])

  // enabled = catalog minus disabled (new values default-on, no seen-tracking)
  const branchEnabled = useMemo(() => new Set(dims.branch.filter((d) => !branchOff.has(d.id)).map((d) => d.id)), [dims.branch, branchOff])
  const sourceEnabled = useMemo(() => new Set(dims.from.filter((d) => !sourceOff.has(d.id)).map((d) => d.id)), [dims.from, sourceOff])
  const typeEnabled = useMemo(() => new Set(dims.type.filter((d) => !typeOff.has(d.id)).map((d) => d.id)), [dims.type, typeOff])

  const enabledFor = (gb) => (gb === 'branch' ? branchEnabled : gb === 'from' ? sourceEnabled : typeEnabled)
  const passes = (r, bypass) =>
    (bypass === 'branch' || branchEnabled.has(String(r.branch))) &&
    (bypass === 'from' || sourceEnabled.has(r.from || '-')) &&
    (bypass === 'type' || typeEnabled.has(String(r.appointmentList)))

  const countByBucket = (rows, bypass) => {
    const acc = {}
    for (const r of rows || []) { if (!passes(r, bypass)) continue; const k = rowKey(gran, r); acc[k] = (acc[k] || 0) + (r.count || 0) }
    return acc
  }

  const top = useMemo(() => {
    if (rangeInvalid || !topReady) return { data: [], series: [] }
    const buckets = bucketsFor(gran, start, end)

    // pre-bucket ortho attach counts (no dimension filtering — it's a plain total)
    const oAcc = {}
    if (isOrtho && orthoTopRows) {
      for (const r of orthoTopRows) { const k = rowKey(gran, r); oAcc[k] = (oAcc[k] || 0) + (r.count || 0) }
    }

    if (mode === 'split') {
      const shown = dims[groupBy].filter((d) => enabledFor(groupBy).has(d.id))
      const series = shown.map((d) => ({ key: d.id, label: d.label, color: d.color }))
      const allow = new Set(shown.map((d) => d.id))
      const acc = {}
      for (const r of topRows) {
        if (!passes(r, null)) continue
        const dv = dimValue(groupBy, r)
        if (!allow.has(dv)) continue
        const k = rowKey(gran, r)
        ;(acc[k] || (acc[k] = {}))[dv] = (acc[k][dv] || 0) + (r.count || 0)
      }
      const data = buckets.map((bk) => { const row = { label: bk.label, full: bk.full }; for (const s of series) row[s.key] = acc[bk.key]?.[s.key] || 0; return row })
      return { data, series }
    }
    if (mode === 'compare') {
      const [ps, pe] = comparePrevRange(preset, start, end)
      const prevBuckets = bucketsFor(gran, ps, pe)
      const cur = countByBucket(topRows, null), prv = countByBucket(cmpReady ? cmpRows : null, null)
      const series = [{ key: 'current', label: 'ช่วงนี้', color: GREEN }, { key: 'previous', label: `${comparePrevLabel(preset)} (คนไข้ใหม่)`, color: SLATE, dashed: true }]
      if (isOrtho) series.push({ key: 'orthoAttach', label: 'ติดเครื่องมือ', color: BLUE })
      const data = buckets.map((bk, i) => {
        const row = { label: bk.label, full: bk.full, current: cur[bk.key] || 0, previous: prv[prevBuckets[i]?.key] || 0 }
        if (isOrtho) row.orthoAttach = oAcc[bk.key] || 0
        return row
      })
      return { data, series }
    }
    const acc = countByBucket(topRows, null)
    const series = [{ key: 'value', label: 'คนไข้ใหม่', color: GREEN }]
    if (isOrtho) series.push({ key: 'orthoAttach', label: 'ติดเครื่องมือ', color: BLUE })
    const data = buckets.map((bk) => {
      const row = { label: bk.label, full: bk.full, value: acc[bk.key] || 0 }
      if (isOrtho) row.orthoAttach = oAcc[bk.key] || 0
      return row
    })
    return { data, series }
  }, [topRows, cmpRows, topReady, cmpReady, mode, groupBy, gran, start, end, preset, rangeInvalid, dims, branchEnabled, sourceEnabled, typeEnabled, isOrtho, orthoTopRows])

  // radar axes for a dimension — respects ALL filters (branch/source/type),
  // so Tooth Taxi (default off) and disabled values drop out.
  const buildRadar = (gb) => {
    const cur = {}, prv = {}
    for (const r of topRows || []) { if (!passes(r, null)) continue; const dv = dimValue(gb, r); cur[dv] = (cur[dv] || 0) + (r.count || 0) }
    if (radarCompare && cmpReady) for (const r of cmpRows || []) { if (!passes(r, null)) continue; const dv = dimValue(gb, r); prv[dv] = (prv[dv] || 0) + (r.count || 0) }
    return dims[gb].filter((d) => enabledFor(gb).has(d.id))
      .map((d) => ({ key: d.id, label: d.label, color: d.color, value: cur[d.id] || 0, prev: prv[d.id] || 0 }))
      .filter((a) => a.value > 0 || (radarCompare && a.prev > 0))
  }
  const radarPrimary = useMemo(() => (isRadar && topReady ? buildRadar(groupBy) : []),
    [isRadar, topReady, topRows, cmpRows, cmpReady, radarCompare, groupBy, dims, branchEnabled, sourceEnabled, typeEnabled])
  const radarTotal = useMemo(() => radarPrimary.reduce((s, a) => s + a.value, 0), [radarPrimary])
  const radarPrevTotal = useMemo(() => radarPrimary.reduce((s, a) => s + (a.prev || 0), 0), [radarPrimary])
  const radarDelta = radarCompare && radarPrevTotal ? ((radarTotal - radarPrevTotal) / radarPrevTotal) * 100 : null

  const topSummary = useMemo(() => {
    const total = (rows) => (rows || []).reduce((s, r) => (passes(r, null) ? s + (r.count || 0) : s), 0)
    const current = total(topRows)
    if (mode !== 'compare') return { current }
    const previous = total(cmpRows)
    const delta = previous ? ((current - previous) / previous) * 100 : null
    return { current, previous, delta }
  }, [topRows, cmpRows, mode, branchEnabled, sourceEnabled, typeEnabled])

  const bottom = useMemo(() => {
    if (!monthRows) return []
    const acc = {}
    for (const r of monthRows) {
      if (r.year < 2000 || !passes(r, null)) continue
      const k = `${r.year}-${pad(r.month)}`
      acc[k] = (acc[k] || 0) + (r.count || 0)
    }
    const oAcc = {}
    if (isOrtho) {
      for (const r of orthoMonthRows || []) {
        const k = `${r.year}-${pad(r.month)}`
        oAcc[k] = (oAcc[k] || 0) + (r.count || 0)
      }
    }
    return enumMonths(BOTTOM_START, today).map(({ year, month }) => {
      const k = `${year}-${pad(month)}`
      const point = { label: `${TH_MON_ABBR[month - 1]} ${String(year).slice(2)}`, full: `${TH_MON_FULL[month - 1]} ${year}`, value: acc[k] || 0 }
      if (isOrtho) point.orthoAttach = oAcc[k] || 0
      return point
    })
  }, [monthRows, branchEnabled, sourceEnabled, typeEnabled, today, isOrtho, orthoMonthRows])
  const bottomSum = useMemo(() => bottom.reduce((a, d) => a + d.value, 0), [bottom])

  const toggler = (setOff) => (id) => setOff((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  // dimension options: a single campaign has no "type" axis (it IS one type)
  const groups = singleCampaign
    ? [{ v: 'branch', label: 'สาขา' }, { v: 'from', label: 'ช่องทาง' }]
    : campaignMode
      ? [{ v: 'branch', label: 'สาขา' }, { v: 'from', label: 'ช่องทาง' }, { v: 'type', label: 'แคมเปญ' }]
      : GROUPS
  const groupLabel = (groups.find((g) => g.v === groupBy) || groups[0]).label
  const titleText = singleCampaign ? `แคมเปญ: ${campaigns[0].label}` : campaignMode ? 'คนไข้ใหม่จากแคมเปญ' : 'คนไข้ใหม่ตามช่วงเวลา'
  const typeFilterLabel = campaignMode ? 'แคมเปญ' : 'ประเภทนัด'

  // export the RAW Xano rows for the selected date range (date filter only —
  // ignores the on-screen branch/source/type/สปสช filters)
  const exportCsv = async () => {
    setExporting(true)
    setExportErr('')
    try {
      const rows = await apiGet('/api/appointment/daily', { start_date: start, end_date: end, branch_id: 0, exclude_nhso: false })
      const list = Array.isArray(rows) ? rows : []
      const header = ['วันที่', 'สาขา', 'ช่องทาง', 'ประเภทนัด', 'จำนวน']
      const body = list.map((r) => [
        `${r.year}-${pad(r.month)}-${pad(r.day)}`,
        branchName[String(r.branch)] || `สาขา #${r.branch}`,
        r.from || '',
        r.appointmentList || '',
        r.count || 0,
      ])
      downloadCsv(`new-patients_raw_${start}_${end}.csv`, [header, ...body])
    } catch (e) {
      setExportErr('ส่งออกไม่สำเร็จ: ' + (e.message || ''))
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <p className="text-[12.5px] text-slate-500 mb-3">
        {campaignMode
          ? 'คนไข้ใหม่จากแคมเปญการตลาด (กรองเฉพาะประเภทที่ทำโฆษณา) · ค่าเริ่มต้นไม่รวม Tooth Taxi'
          : 'คนไข้ใหม่ = ผู้ป่วยที่ลงทะเบียนใหม่และมีนัดในช่วงเวลา · ค่าเริ่มต้นไม่รวม Tooth Taxi (ไม่ใช่ลีดจากโฆษณา)'}
      </p>

      {/* filters — all dropdowns, affect both charts */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="text-[12.5px] text-slate-500 mr-0.5">ตัวกรอง</span>
        <MultiSelect label="สาขา" items={dims.branch} enabled={branchEnabled}
          onToggle={toggler(setBranchOff)} onAll={() => setBranchOff(new Set())} onClear={() => setBranchOff(new Set(dims.branch.map((d) => d.id)))} />
        <MultiSelect label="ช่องทาง" items={dims.from} enabled={sourceEnabled}
          onToggle={toggler(setSourceOff)} onAll={() => setSourceOff(new Set())} onClear={() => setSourceOff(new Set(dims.from.map((d) => d.id)))} />
        {!singleCampaign && (
          <MultiSelect label={typeFilterLabel} items={dims.type} enabled={typeEnabled} search
            onToggle={toggler(setTypeOff)} onAll={() => setTypeOff(new Set())} onClear={() => setTypeOff(new Set(dims.type.map((d) => d.id)))} />
        )}
      </div>

      <div className="space-y-6">
        <Card
          icon={<CalendarDays size={18} strokeWidth={1.75} />}
          title={titleText}
          badge={isRadar ? `เรดาร์ราย${groupLabel}` : gran === 'month' ? 'รายเดือน' : 'รายวัน'}
          subtitle={isRadar ? `เปรียบเทียบคนไข้ใหม่แต่ละ${groupLabel}` : gran === 'month' ? `ช่วงยาว (${rangeDays} วัน) — แสดงแบบรายเดือน` : 'แยกเป็นรายวัน'}
          right={(
            <div className="flex flex-wrap items-center gap-2">
              <DateRange start={start} end={end} today={today} onStart={(v) => { setStart(v); setPreset('') }} onEnd={(v) => { setEnd(v); setPreset('') }} />
              {!campaignMode && (
                <button onClick={exportCsv} disabled={exporting} title="ส่งออกข้อมูลดิบช่วงวันที่ที่เลือกเป็น CSV"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:border-slate-300 disabled:opacity-50">
                  <Download size={14} strokeWidth={1.9} /> {exporting ? 'กำลังส่งออก…' : 'CSV'}
                </button>
              )}
              {!campaignMode && exportErr && (
                <span role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-[12px] text-rose-700">{exportErr}</span>
              )}
            </div>
          )}
        >
          <PresetBar preset={preset} onPick={applyPreset} />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-[12px] text-slate-500">แยกตาม</span>
            <Segmented value={groupBy} onChange={setGroupBy} options={groups.map((g) => ({ v: g.v, label: g.label }))} />
            <span className="w-px h-5 bg-slate-200 mx-1" />
            {isRadar ? (
              <Toggle on={radarCompare} onClick={() => setRadarCompare((v) => !v)}>{compareLabel(preset)}</Toggle>
            ) : (
              <Segmented value={mode} onChange={setMode} options={[{ v: 'sum', label: 'รวม' }, { v: 'split', label: `แยก${groupLabel}` }, { v: 'compare', label: compareLabel(preset) }]} />
            )}
          </div>

          {rangeInvalid ? (
            <Notice text="วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น" tone="warn" />
          ) : topState.loading || !topReady || (wantCompare && (cmpState.loading || !cmpReady)) || (isOrtho && (orthoTopState.loading || !orthoTopReady)) ? (
            <ChartLoading />
          ) : topState.err ? (
            <Notice text={topState.err} tone="error" />
          ) : isRadar ? (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-1">
                <Stat label="คนไข้ใหม่" value={fmt(radarTotal)} color={GREEN} />
                {radarCompare && <Stat label={comparePrevLabel(preset)} value={fmt(radarPrevTotal)} color={SLATE} />}
                {radarDelta != null && <Delta pct={radarDelta} />}
                <span className="text-[12px] text-slate-400 pb-1">{radarPrimary.length} {groupLabel}</span>
              </div>
              {groupBy === 'type' ? (
                <BarChartH data={radarPrimary} color={GREEN} prevColor={SLATE} compare={radarCompare} />
              ) : (
                <RadarChart axes={radarPrimary} color={GREEN} prevColor={SLATE} compare={radarCompare} height={380} />
              )}
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                <Stat label="คนไข้ใหม่" value={fmt(topSummary.current)} color={GREEN} />
                {isOrtho && mode !== 'split' && <Stat label="ติดเครื่องมือ" value={fmt(orthoTopSum)} color={BLUE} />}
                {mode === 'compare' && (
                  <>
                    <Stat label={comparePrevLabel(preset)} value={fmt(topSummary.previous)} color={SLATE} />
                    {topSummary.delta != null && <Delta pct={topSummary.delta} />}
                  </>
                )}
                <span className="text-[12px] text-slate-400 pb-1">{top.data.length} {gran === 'month' ? 'เดือน' : 'วัน'}</span>
              </div>
              <RevenueChart data={top.data} series={top.series} height={310} showLegend={mode !== 'split'} compact={false} />
            </>
          )}
        </Card>

        <Card icon={<BarChart3 size={18} strokeWidth={1.75} />} title="คนไข้ใหม่รายเดือน" subtitle="ทั้งหมด ตั้งแต่เริ่มเก็บข้อมูล (ตามตัวกรองด้านบน)">
          {monthState.loading || (isOrtho && orthoMonthState.loading) ? (
            <ChartLoading />
          ) : monthState.err ? (
            <Notice text={monthState.err} tone="error" />
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                <Stat label="คนไข้ใหม่ทั้งหมด" value={fmt(bottomSum)} color={GREEN} />
                {isOrtho && <Stat label="ติดเครื่องมือ" value={fmt(orthoMonthSum)} color={BLUE} />}
                <span className="text-[12px] text-slate-400 pb-1">{bottom.length} เดือน</span>
              </div>
              <RevenueChart
                data={bottom}
                series={isOrtho
                  ? [{ key: 'value', label: 'คนไข้ใหม่', color: GREEN }, { key: 'orthoAttach', label: 'ติดเครื่องมือ', color: BLUE }]
                  : [{ key: 'value', label: 'คนไข้ใหม่', color: GREEN }]
                }
                height={320} compact={false} labelAll
              />
            </>
          )}
        </Card>
      </div>
    </>
  )
}
